// Reports.js - Updated to use API service

import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  DocumentTextIcon,
  PrinterIcon,
  ShareIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { format, subDays } from 'date-fns';
import toast from 'react-hot-toast';
import { adminAPI } from '../../services/api';

// Import Chart.js properly
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
);

// Dynamic imports for chart components to avoid SSR issues
const Line = React.lazy(() => import('react-chartjs-2').then(module => ({ default: module.Line })));
const Bar = React.lazy(() => import('react-chartjs-2').then(module => ({ default: module.Bar })));
const Doughnut = React.lazy(() => import('react-chartjs-2').then(module => ({ default: module.Doughnut })));

const Reports = () => {
  const [reportType, setReportType] = useState('usage');
  const [dateRange, setDateRange] = useState('7days');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    start: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });
  
  // Refs for chart instances
  const chartRefs = useRef({});

  // const reportTypes = [
  //   { id: 'usage', name: 'Usage Report', icon: ChartBarIcon, color: 'blue' },
  //   { id: 'users', name: 'User Activity', icon: UserGroupIcon, color: 'green' },
  //   { id: 'messages', name: 'Message Analytics', icon: ChatBubbleLeftRightIcon, color: 'purple' },
  //   { id: 'performance', name: 'Performance', icon: ArrowTrendingUpIcon, color: 'purple' },
  // ];

  // const dateRanges = [
  //   { value: '24hours', label: 'Last 24 Hours' },
  //   { value: '7days', label: 'Last 7 Days' },
  //   { value: '30days', label: 'Last 30 Days' },
  //   { value: 'thisMonth', label: 'This Month' },
  //   { value: 'lastMonth', label: 'Last Month' },
  //   { value: 'custom', label: 'Custom Range' },
  // ];

  useEffect(() => {
    loadReportData();
    
    // Cleanup function to destroy charts
    return () => {
      Object.keys(chartRefs.current).forEach(key => {
        if (chartRefs.current[key]) {
          chartRefs.current[key].destroy();
        }
      });
    };
  }, []);

  useEffect(() => {
    loadReportData();
  }, [reportType, dateRange]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      let response;
      if (dateRange === 'custom') {
        response = await adminAPI.getReports(
          reportType, 
          dateRange, 
          customDateRange.start, 
          customDateRange.end
        );
      } else {
        response = await adminAPI.getReports(reportType, dateRange);
      }
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to load report data:', error);
      toast.error('Failed to load report data');
      // Set mock data for demonstration
      setReportData(generateMockData(reportType));
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = (type) => {
    const days = dateRange === '24hours' ? 1 : 
                 dateRange === '7days' ? 7 : 
                 dateRange === '30days' ? 30 : 30;
    
    const dates = Array.from({ length: days }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (days - 1 - i));
      return format(date, 'MMM dd');
    });

    return {
      dates,
      apiCalls: dates.map(() => Math.floor(Math.random() * 100) + 50),
      messages: dates.map(() => Math.floor(Math.random() * 80) + 20),
      users: dates.map(() => Math.floor(Math.random() * 30) + 10),
      totalCalls: 45200,
      totalUsers: 1234,
      avgResponse: 234,
      successRate: 99.2,
      topUsers: [
        { name: 'John Doe', messages: 450, apiCalls: 890, lastActive: '2 min ago' },
        { name: 'Jane Smith', messages: 380, apiCalls: 760, lastActive: '5 min ago' },
        { name: 'Bob Johnson', messages: 320, apiCalls: 640, lastActive: '10 min ago' },
        { name: 'Alice Brown', messages: 290, apiCalls: 580, lastActive: '15 min ago' },
        { name: 'Charlie Wilson', messages: 250, apiCalls: 500, lastActive: '20 min ago' },
      ]
    };
  };

  const exportReport = async (format = 'pdf') => {
    setExporting(true);
    try {
      let response;
      if (dateRange === 'custom') {
        response = await adminAPI.exportReport(
          reportType, 
          dateRange, 
          format, 
          customDateRange.start, 
          customDateRange.end
        );
      } else {
        response = await adminAPI.exportReport(reportType, dateRange, format);
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}-report-${Date.now()}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const printReport = () => {
    window.print();
  };

  const shareReport = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Share link copied to clipboard');
  };

  // Chart configurations with unique IDs
  const getUsageChartData = () => ({
    labels: reportData?.dates || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'API Calls',
        data: reportData?.apiCalls || [65, 59, 80, 81, 56, 55, 40],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
      {
        label: 'Messages',
        data: reportData?.messages || [28, 48, 40, 19, 86, 27, 90],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: 'rgb(16, 185, 129)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      }
    ]
  });

  const getUserActivityData = () => ({
    labels: ['Active', 'Inactive', 'New'],
    datasets: [
      {
        data: [65, 25, 10],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(156, 163, 175, 0.8)',
          'rgba(16, 185, 129, 0.8)',
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(156, 163, 175)',
          'rgb(16, 185, 129)',
        ],
        borderWidth: 1,
        hoverOffset: 4,
      },
    ],
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          boxWidth: 6,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    animation: {
      duration: 2000,
      easing: 'easeInOutQuart',
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          padding: 20,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
      },
    },
    cutout: '60%',
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 2000,
    },
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-xl shadow-md p-6 h-32"></div>
              ))}
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 h-96 mb-8"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reports & Analytics</h1>
          <p className="text-gray-600">Generate and export detailed reports about your system</p>
        </motion.div>

        {/* Report Type Selector */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-100"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {reportTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setReportType(type.id)}
                  className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    reportType === type.id
                      ? `bg-${type.color}-600 text-white shadow-lg scale-105`
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <type.icon className="h-4 w-4 mr-2" />
                  {type.name}
                </button>
              ))}
            </div>

            <div className="flex items-center space-x-3">
           
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
              >
                {dateRanges.map(range => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>

              {dateRange === 'custom' && (
                <div className="flex items-center space-x-2">
                  <input
                    type="date"
                    value={customDateRange.start}
                    onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="date"
                    value={customDateRange.end}
                    onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}

       
              <button
                onClick={loadReportData}
                className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                title="Refresh"
              >
                <ArrowPathIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>
        </motion.div> */}

        {/* Report Content */}
        <React.Suspense fallback={<div>Loading charts...</div>}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
              >
                <p className="text-sm text-gray-500 mb-2">Total API Calls</p>
                <p className="text-3xl font-bold text-gray-900">
                  {reportData?.totalCalls?.toLocaleString() || '0'}
                </p>
                <span className="inline-flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full mt-2">
                  ↑ 12.5%
                </span>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
              >
                <p className="text-sm text-gray-500 mb-2">Active Users</p>
                <p className="text-3xl font-bold text-gray-900">
                  {reportData?.totalUsers?.toLocaleString() || '0'}
                </p>
                <span className="inline-flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full mt-2">
                  ↑ 5.2%
                </span>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
              >
                <p className="text-sm text-gray-500 mb-2">Avg Response Time</p>
                <p className="text-3xl font-bold text-gray-900">
                  {reportData?.avgResponse || '0'}ms
                </p>
                <span className="inline-flex items-center text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full mt-2">
                  ↑ 2.1%
                </span>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
              >
                <p className="text-sm text-gray-500 mb-2">Success Rate</p>
                <p className="text-3xl font-bold text-gray-900">
                  {reportData?.successRate || '99.2'}%
                </p>
                <span className="inline-flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full mt-2">
                  ↑ 0.3%
                </span>
              </motion.div>
            </div>

            {/* Main Chart */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
            >
              <div className="flex justify-between items-center mb-6">
                {/* <h2 className="text-lg font-semibold text-gray-900">
                  {reportTypes.find(t => t.id === reportType)?.name}
                </h2> */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={printReport}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Print"
                  >
                    <PrinterIcon className="h-5 w-5 text-gray-600" />
                  </button>
                  <button
                    onClick={shareReport}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Share"
                  >
                    <ShareIcon className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>
              <div className="h-96" id="main-chart-container">
                <Line 
                  key={`main-chart-${reportType}-${dateRange}`}
                  data={getUsageChartData()} 
                  options={chartOptions}
                  redraw={true}
                />
              </div>
            </motion.div>

            {/* Secondary Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* User Distribution */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
              >
                <h3 className="text-md font-semibold text-gray-900 mb-4">User Distribution</h3>
                <div className="h-80" id="doughnut-chart-container">
                  <Doughnut 
                    key={`doughnut-${reportType}`}
                    data={getUserActivityData()} 
                    options={doughnutOptions}
                    redraw={true}
                  />
                </div>
              </motion.div>

              {/* Top Users Table */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
              >
                <h3 className="text-md font-semibold text-gray-900 mb-4">Top Users by Activity</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase">Messages</th>
                        <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase">API Calls</th>
                        <th className="text-left py-3 text-xs font-medium text-gray-500 uppercase">Last Active</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(reportData?.topUsers || generateMockData(reportType).topUsers).map((user, i) => (
                        <motion.tr 
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="py-3 text-sm font-medium text-gray-900">{user.name}</td>
                          <td className="py-3 text-sm text-gray-600">{user.messages}</td>
                          <td className="py-3 text-sm text-gray-600">{user.apiCalls}</td>
                          <td className="py-3 text-sm text-gray-400">{user.lastActive}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </div>

            {/* Detailed Table */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
            >
              <h3 className="text-md font-semibold text-gray-900 mb-4">Detailed Usage Log</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">API Calls</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Messages</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active Users</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Response</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(reportData?.dates || []).map((date, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{date}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{reportData?.apiCalls?.[index] || 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{reportData?.messages?.[index] || 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{reportData?.users?.[index] || 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{Math.floor(Math.random() * 200)}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Export Options */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
            >
              <h3 className="text-md font-semibold text-gray-900 mb-4">Export Options</h3>
              <div className="flex flex-wrap gap-3">
                {/* <button
                  onClick={() => exportReport('pdf')}
                  disabled={exporting}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  Export as PDF
                </button> */}
                <button
                  onClick={() => exportReport('csv')}
                  disabled={exporting}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  Export as CSV
                </button>
                <button
                  onClick={() => exportReport('excel')}
                  disabled={exporting}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  Export as Excel
                </button>
              </div>
            </motion.div>
          </motion.div>
        </React.Suspense>
      </div>
    </div>
  );
};

export default Reports;